// novem atom — status filter pill bar (NovemHR-ekranlar port).
//
// B2 (tenant health), B3 (license state), B4 (flag stage) each rendered
// the same "row of rounded pills, one selected, optional count badge"
// filter by hand with plain <button>s — single-select but with no group
// semantics. This promotes it WITH the correct a11y model: a radiogroup
// (single choice among the pills) with roving focus + arrow navigation,
// matching the library's RadioGroup keyboard model.
//
// API (per the call sites' real usage):
//   * items: { value, label, count? } — count renders as a trailing badge.
//   * value / onChange — controlled single selection.
//   * "aria-label" — names the group (a radiogroup needs an accessible
//     name; there is no visible <legend> in the filter-bar layout).
//
// Accessibility (WAI-ARIA radiogroup):
//   * Container role="radiogroup" + aria-label.
//   * Each pill role="radio" + aria-checked; only the selected pill (or
//     the first when none selected) is in the tab order (roving tabindex).
//   * Arrow Left/Up + Right/Down move selection + focus; Home/End jump to
//     ends. Space/Enter selects the focused pill.

import {
  forwardRef,
  useCallback,
  useRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export interface FilterPillItem<V extends string = string> {
  readonly value: V;
  readonly label: ReactNode;
  /** Optional trailing count badge. */
  readonly count?: number;
}

export interface FilterPillBarProps<V extends string = string>
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  readonly items: ReadonlyArray<FilterPillItem<V>>;
  readonly value: V;
  readonly onChange: (value: V) => void;
  /** Accessible name for the radiogroup (required — no visible legend). */
  readonly "aria-label": string;
}

function FilterPillBarInner<V extends string = string>(
  { items, value, onChange, "aria-label": ariaLabel, className, ...rest }: FilterPillBarProps<V>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback(
    (node: HTMLDivElement | null): void => {
      containerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref !== null) ref.current = node;
    },
    [ref],
  );

  const focusAndSelect = useCallback(
    (idx: number): void => {
      const target = items[idx];
      if (target === undefined) return;
      onChange(target.value);
      const pills = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
      pills?.[idx]?.focus();
    },
    [items, onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, idx: number): void => {
      let next = -1;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") next = (idx + 1) % items.length;
      else if (event.key === "ArrowLeft" || event.key === "ArrowUp")
        next = (idx - 1 + items.length) % items.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = items.length - 1;
      if (next >= 0) {
        event.preventDefault();
        focusAndSelect(next);
      }
    },
    [items, focusAndSelect],
  );

  // Roving tabindex anchor — selected pill, or the first when the current
  // value isn't among the items (defensive).
  const selectedIdx = items.findIndex((it) => it.value === value);
  const tabbableIdx = selectedIdx >= 0 ? selectedIdx : 0;

  return (
    <div
      ref={setRef}
      role="radiogroup"
      aria-label={ariaLabel}
      data-component="filter-pill-bar"
      className={cx("nai-filter-pill-bar", className)}
      {...rest}
    >
      {items.map((item, idx) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={idx === tabbableIdx ? 0 : -1}
            data-selected={selected || undefined}
            className="nai-filter-pill"
            onClick={(): void => onChange(item.value)}
            onKeyDown={(e): void => handleKeyDown(e, idx)}
          >
            {item.label}
            {item.count !== undefined && (
              <span className="nai-filter-pill-count" aria-hidden="true">
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Generic forwardRef wrapper. The cast preserves the `<V>` value-union
 * generic through forwardRef (which otherwise erases it) so call sites
 * keep narrow typing on value/onChange (same pattern as DataTable).
 */
export const FilterPillBar = forwardRef(FilterPillBarInner) as <V extends string = string>(
  props: FilterPillBarProps<V> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
